import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ExternalLink, Pencil, Trash2 } from 'lucide-react';
import { deleteArticle } from '@mkcore/support/schema';
import { Badge, Button, Card, ErrorState, PageHeader, Skeleton } from '@mkcore/ui';
import { ConfirmButton } from '@/components/confirm';
import { Markdown } from '@/components/markdown';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { ArticleDialog, articleQuery } from '@/features/support/articles';
import { TicketStatusBadge } from '@/features/support/tickets';

export const Route = createFileRoute('/_app/support/articles/$id')({ component: ArticlePage });

/** One knowledge article (CO-0495): the markdown, its links, the tickets it helped; change or delete it. */
function ArticlePage() {
  const { id } = Route.useParams();
  const { t } = useTranslation();
  const message = useErrorMessage();
  const navigate = useNavigate();
  const canWrite = useCan('support:update');
  const canDelete = useCan('support:delete');
  const [editing, setEditing] = useState(false);
  const q = useQuery(articleQuery(id));
  const remove = useActionMutation(deleteArticle, { invalidate: [['support', 'article']], success: t('articles.deleted'), onSuccess: () => void navigate({ to: '/support/articles' }) });
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  const a = q.data;
  return (
    <>
      <Link to="/support/articles" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('articles.title')}
      </Link>
      <PageHeader
        title={a.title}
        description={
          <span className="flex flex-wrap items-center gap-2">
            {a.category && <Badge tone="outline">{a.category}</Badge>}
            <Badge tone={a.organisationName ? 'info' : 'neutral'}>{a.organisationName ?? t('articles.forEveryone')}</Badge>
            <Badge tone={a.audience === 'internal' ? 'neutral' : 'success'}>{t(`articles.audience.${a.audience}`)}</Badge>
            {a.tags.map((tag) => (
              <Badge key={tag} tone="outline">
                #{tag}
              </Badge>
            ))}
            <span className="text-fg-3">{formatDateTime(a.updatedAt)}</span>
          </span>
        }
        actions={
          <span className="flex gap-2">
            {canWrite && (
              <Button variant="secondary" onClick={() => setEditing(true)}>
                <Pencil /> {t('articles.edit')}
              </Button>
            )}
            {canDelete && (
              <ConfirmButton
                variant="danger"
                icon={<Trash2 />}
                label={t('common.delete')}
                title={t('articles.deleteTitle', { title: a.title })}
                description={t('articles.deleteHint')}
                onConfirm={() => remove.mutateAsync({ input: { id: a.id }, ifMatch: a.version })}
              />
            )}
          </span>
        }
      />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card className="p-5">
          <Markdown text={a.body} />
        </Card>
        <div className="grid content-start gap-4">
          {!!a.links.length && (
            <Card className="grid gap-2 p-4">
              <h2 className="text-sm font-bold">{t('articles.fields.links')}</h2>
              <ul className="grid gap-1 text-sm">
                {a.links.map((l) => (
                  <li key={l.url}>
                    <a href={l.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">
                      <ExternalLink className="size-3.5" /> {l.title}
                    </a>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          <Card className="grid gap-2 p-4">
            <h2 className="text-sm font-bold">{t('articles.helpedTickets')}</h2>
            <ul className="grid gap-1 text-sm" aria-label={t('articles.helpedTickets')}>
              {a.tickets.map((tk) => (
                <li key={tk.id} className="flex items-center gap-2">
                  <span className="text-fg-3 tabular">{tk.key}</span>
                  <Link to="/support/$id" params={{ id: tk.id }} className="underline">
                    {tk.subject}
                  </Link>
                  <TicketStatusBadge status={tk.status} />
                </li>
              ))}
              {!a.tickets.length && <li className="text-fg-3">{t('articles.noTickets')}</li>}
            </ul>
          </Card>
        </div>
      </div>
      {editing && <ArticleDialog article={a} onClose={() => setEditing(false)} />}
    </>
  );
}
